import {
	useMutation,
	useMutationState,
	useQueryClient,
} from "@tanstack/react-query";
import { z } from "zod";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";

// The endpoint acknowledges with a status code only (no response body).
const generateLoadDocumentsResponseSchema = z.undefined();

const GENERATE_DOCUMENTS_ERROR_MESSAGE =
	"Failed to generate Bill of Lading and labels. Please try again.";

export const GENERATE_LOAD_DOCUMENTS_MUTATION_KEY = [
	"generateLoadDocuments",
] as const;

/**
 * Regenerates the BOL and shipping labels for a load. The mutation rejects on
 * failure so callers awaiting `mutateAsync` only continue on success; the
 * user-facing error and cache refresh live on the mutation itself so they run
 * for every request, even after this observer has started another one.
 */
export const useGenerateLoadDocuments = () => {
	const queryClient = useQueryClient();
	const { showError } = useNotificationActions();

	return useMutation({
		mutationKey: GENERATE_LOAD_DOCUMENTS_MUTATION_KEY,
		mutationFn: async (poNumber: number): Promise<void> => {
			const result = await postAndValidate(
				apiRoutes.GENERATELOADDOCUMENTS.replace(":poNumber", String(poNumber)),
				undefined,
				generateLoadDocumentsResponseSchema,
			);
			if (!result.success) {
				throw new Error(result.error || GENERATE_DOCUMENTS_ERROR_MESSAGE);
			}
		},
		onSuccess: async (_data, poNumber) => {
			// Regeneration can overwrite the currently selected file under the same
			// name, so refresh both the list and any open document's content.
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: ["invoiceDocuments", poNumber],
				}),
				queryClient.invalidateQueries({
					queryKey: ["selectedInvoiceDocument", poNumber],
				}),
			]);
		},
		onError: (error) => {
			console.error("Generate load documents request failed:", error);
			showError(error.message || GENERATE_DOCUMENTS_ERROR_MESSAGE);
		},
	});
};

/**
 * PO numbers with a regeneration request in flight. Read from the shared
 * mutation cache (not a single observer), so overlapping requests for
 * different loads are all tracked.
 */
export const usePendingLoadDocumentGenerations = (): number[] =>
	useMutationState({
		filters: {
			mutationKey: GENERATE_LOAD_DOCUMENTS_MUTATION_KEY,
			status: "pending",
		},
		select: (mutation) => mutation.state.variables as number,
	});
